import { FC, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { DeleteInfoBlockItemModal, EditInfoBlockItemModal } from "Bands/containers";
import { usePermissionsContext } from "Bands/contexts";
import { dataMetaSelector } from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";
import { useGetInfoBlockItems } from "Bands/utils";
import { Marker } from "shared/assets/icons";
import { Card, InfoBlockItems, Message } from "shared/components";
import { IAPIState, IContact, IInfoBlockItem, IPlace } from "shared/types";

import {
  getInfoBlockItemReadableName,
  getInfoBlockItemTeaser,
  useEditInfoBlockItems,
} from "shared/utils";
import { copy } from "shared/assets";

interface InfoBlockItemsOverviewProps {
  title: string;
  type: IInfoBlockItem;
}

export const InfoBlockItemsOverview: FC<InfoBlockItemsOverviewProps> = ({ title, type }) => {
  const { bandPermissions } = usePermissionsContext();

  const { apiMeta, items, pagination, fetchItems } = useGetInfoBlockItems(type);

  const {
    selectedItem,
    openEditModal,
    setOpenEditModal,
    openDeleteModal,
    setOpenDeleteModal,
    getItemActions,
  } = useEditInfoBlockItems({
    userPermissions: bandPermissions || {},
    defaultValues:
      type === IInfoBlockItem.place
        ? { places: items as IPlace[] }
        : { contacts: items as IContact[] },
  });

  const { api: deleteItemApiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.deleteInfoBlockItem)
  );

  const itemReadableName = getInfoBlockItemReadableName(type);

  useEffect(() => {
    if (deleteItemApiMeta?.status === IAPIState.succeeded) {
      fetchItems();
    }
  }, [deleteItemApiMeta, fetchItems]);

  const showEmptyMessage = useMemo(() => {
    if (apiMeta?.status === IAPIState.pending) return false;

    return !items.length;
  }, [apiMeta?.status, items.length]);

  return (
    <Card title={title} icon={Marker}>
      {!!items.length && (
        <InfoBlockItems
          type={type}
          items={items}
          Component={getInfoBlockItemTeaser(type)}
          getItemActions={getItemActions}
          loading={apiMeta?.status === IAPIState.pending}
          pager={
            pagination && {
              ...pagination,
              onPageChange: fetchItems,
            }
          }
        />
      )}
      {showEmptyMessage && (
        <Message>{copy.bandAssets.messages.noItems.replace("%s", itemReadableName)}</Message>
      )}
      {openEditModal && selectedItem && (
        <EditInfoBlockItemModal
          type={openEditModal}
          item={selectedItem}
          handleClose={() => setOpenEditModal(null)}
        />
      )}
      {openDeleteModal && selectedItem && (
        <DeleteInfoBlockItemModal
          deleteItem={selectedItem}
          type={openDeleteModal}
          handleClose={() => setOpenDeleteModal(null)}
        />
      )}
    </Card>
  );
};
